<template>
  <v-container fluid class="pa-0">
    <!-- ==================== HERO (dynamique) ==================== -->
    <v-sheet color="primary" dark class="hero-header pa-6 pa-md-10">
      <v-row align="center" justify="center">
        <v-col cols="12" md="8" class="text-center">
          <v-avatar size="64" color="white" class="mb-4 elevation-4">
            <v-icon size="36" color="primary">
              {{ view === 'audience' ? 'mdi-account-group' : 'mdi-send-check' }}
            </v-icon>
          </v-avatar>
          <h1 class="text-h4 text-md-h3 font-weight-bold white--text mb-2">
            {{ view === 'audience' ? 'New Broadcast' : 'Send Broadcast' }}
          </h1>
          <p class="text-subtitle-1 white--text text--lighten-3 mb-0">
            {{ view === 'audience'
              ? 'Choose your audience to start the broadcast'
              : 'Select channels, compose your message, pick prospects and send' }}
          </p>
        </v-col>
      </v-row>
    </v-sheet>

    <!-- ==================== ÉTAPE 1 : AUDIENCE ==================== -->
    <template v-if="view === 'audience'">
      <v-container class="py-8">
        <v-row justify="center">
          <v-col cols="12" md="6" lg="4">
            <v-card elevation="3" rounded="xl" class="pa-6">
              <v-card-title class="text-h5 font-weight-bold justify-center mb-4">
                <v-icon large color="primary" class="mr-2">mdi-account-group</v-icon>
                Target Audience
              </v-card-title>
              <v-card-text>
                <v-select
                  v-model="selectedAudience"
                  :items="adGroups"
                  item-text="name"
                  item-value="id"
                  label="Prospects List *"
                  placeholder="Choose a group..."
                  outlined
                  dense
                  prepend-inner-icon="mdi-account-multiple"
                  :rules="[v => !!v || 'Required']"
                  class="mb-4"
                ></v-select>

                <v-expand-transition>
                  <v-alert
                    v-if="selectedAudience && audienceInfo"
                    color="primary"
                    outlined
                    dense
                    icon="mdi-information"
                    class="mb-4"
                  >
                    <span class="font-weight-medium">{{ audienceInfo.name }}</span>
                    <v-chip x-small class="ml-2" color="primary" dark>
                      {{ audienceInfo.count || '?' }} prospects
                    </v-chip>
                  </v-alert>
                </v-expand-transition>
              </v-card-text>
              <v-card-actions class="justify-center">
                <v-btn
                  color="primary"
                  x-large
                  depressed
                  :disabled="!selectedAudience"
                  @click="startCampaign"
                  class="px-8"
                  rounded
                >
                  <v-icon left>mdi-rocket-launch</v-icon>
                  Load Broadcast
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </template>

    <!-- ==================== ÉTAPE 2 : ENVOI (tout en un) ==================== -->
    <template v-if="view === 'send'">
      <v-container class="py-4 py-md-6 px-2 px-sm-4">
        <v-row justify="center">
          <v-col cols="12" xl="10">

            <!-- ========== UNIFIED CAMPAIGN CARD ========== -->
            <v-card class="mb-6 unified-campaign-card" elevation="3" rounded="xl">



              <!-- ── Section 1: Channels ── -->
              <div class="pa-3 pa-sm-4 pa-md-6">
                <div class="d-flex align-center mb-3 mb-md-4 flex-wrap">
                  <v-avatar size="32" :class="$vuetify.breakpoint.smAndUp ? 'mr-3' : 'mr-2'" color="primary lighten-5">
                    <v-icon color="primary" small>mdi-send</v-icon>
                  </v-avatar>
                  <div class="flex-grow-1" style="min-width: 0;">
                    <div class="text-subtitle-2 text-sm-subtitle-1 font-weight-bold">Messaging Channels</div>
                    <div class="text-caption grey--text d-none d-sm-block">Select the channels through which to send your message</div>
                  </div>
                  <v-chip v-if="selectedChannels.length" x-small small color="primary" dark class="ml-auto">
                    {{ selectedChannels.length }} channel{{ selectedChannels.length > 1 ? 's' : '' }}
                  </v-chip>
                </div>

                <v-item-group v-model="selectedChannels" multiple>
                  <v-row dense>
                    <v-col
                      v-for="ch in channels"
                      :key="ch.value"
                      cols="3" sm="3" md="3"
                    >
                      <v-item v-slot="{ active, toggle }" :value="ch.value">
                        <v-card
                          :color="active ? ch.color + ' lighten-5' : 'grey lighten-4'"
                          :class="['channel-card', active ? 'channel-card--active' : '']"
                          @click="toggle"
                          outlined
                          flat
                        >
                          <v-card-text class="pa-2 pa-sm-3 text-center">
                            <v-icon
                              :color="active ? ch.color : 'grey'"
                              :size="$vuetify.breakpoint.xsOnly ? 22 : 28"
                              class="mb-1 mb-sm-2"
                            >{{ ch.icon }}</v-icon>
                            <div
                              :class="['channel-label font-weight-medium', active ? ch.color + '--text' : 'grey--text']"
                            >
                              {{ ch.label }}
                            </div>
                            <v-fade-transition>
                              <v-icon
                                v-if="active"
                                color="success"
                                :size="$vuetify.breakpoint.xsOnly ? 12 : 16"
                                class="check-icon"
                              >mdi-check-circle</v-icon>
                            </v-fade-transition>
                          </v-card-text>
                        </v-card>
                      </v-item>
                    </v-col>
                  </v-row>
                </v-item-group>

                <v-alert
                  v-if="selectedChannels.length === 0"
                  dense text type="warning"
                  class="mt-3 mb-0"
                  icon="mdi-alert"
                >
                  Select at least one channel to send messages.
                </v-alert>
              </div>

              <v-divider></v-divider>

              <!-- ── Section 2: Message (Template vs Custom tabs) ── -->
              <div class="pa-3 pa-sm-4 pa-md-6">
                <div class="d-flex align-center mb-3 mb-md-4 flex-wrap">
                  <v-avatar size="32" :class="$vuetify.breakpoint.smAndUp ? 'mr-3' : 'mr-2'" color="deep-purple lighten-5">
                    <v-icon color="deep-purple" small>mdi-pencil-box</v-icon>
                  </v-avatar>
                  <div class="flex-grow-1" style="min-width: 0;">
                    <div class="text-subtitle-2 text-sm-subtitle-1 font-weight-bold">Compose Message</div>
                    <div class="text-caption grey--text d-none d-sm-block">Use a template or write your own message</div>
                  </div>
                  <v-chip
                    x-small
                    :color="hasMessage ? 'success' : 'grey lighten-2'"
                    :text-color="hasMessage ? 'white' : 'grey'"
                    class="ml-auto"
                  >
                    <v-icon left x-small>{{ hasMessage ? 'mdi-check' : 'mdi-pencil' }}</v-icon>
                    {{ hasMessage ? 'Ready' : 'Draft' }}
                  </v-chip>
                </div>

                <!-- Tabs: Template vs Custom -->
                <v-tabs
                  v-model="messageMode"
                  background-color="grey lighten-4"
                  color="primary"
                  centered
                  grow
                  class="rounded-lg mb-3 mb-md-4 message-tabs"
                  :height="$vuetify.breakpoint.xsOnly ? 38 : 44"
                >
                  <v-tab class="text-none font-weight-medium tab-label">
                    <v-icon left small>mdi-text-box-multiple</v-icon>
                    <span class="d-none d-sm-inline">Use a </span>Template
                  </v-tab>
                  <v-tab class="text-none font-weight-medium tab-label">
                    <v-icon left small>mdi-pencil</v-icon>
                    <span class="d-none d-sm-inline">Write </span>Custom<span class="d-none d-sm-inline"> Message</span>
                  </v-tab>
                </v-tabs>

                <v-tabs-items v-model="messageMode" class="transparent">
                  <!-- Tab 1: Template -->
                  <v-tab-item>
                    <v-card outlined class="pa-3 pa-sm-4 rounded-lg">
                      <v-select
                        v-model="selectedMessageTemplate"
                        :items="adMessages"
                        item-text="title"
                        item-value="id"
                        label="Select a message template"
                        placeholder="Choose a template..."
                        outlined
                        dense
                        prepend-inner-icon="mdi-text-box"
                        hide-details
                        class="mb-2"
                        @change="applyMessageTemplate"
                      ></v-select>
                      <v-expand-transition>
                        <div v-if="selectedMessageTemplate">
                          <v-alert color="primary" outlined dense icon="mdi-check-circle" class="mt-3 mb-0">
                            Template applied! Preview below.
                          </v-alert>
                        </div>
                      </v-expand-transition>
                      <div v-if="!selectedMessageTemplate" class="text-center py-3 py-sm-4">
                        <v-icon :size="$vuetify.breakpoint.xsOnly ? 36 : 48" color="grey lighten-1" class="mb-2">mdi-text-box-search-outline</v-icon>
                        <div class="text-body-2 grey--text">Select a template to auto-fill all message fields</div>
                      </div>
                    </v-card>
                  </v-tab-item>

                  <!-- Tab 2: Custom Message -->
                  <v-tab-item>
                    <v-card outlined class="pa-3 pa-sm-4 rounded-lg">
                      <v-row dense>
                        <v-col cols="12" sm="6" md="4">
                          <v-text-field
                            v-model="advertising_message.title"
                            label="Subject *"
                            dense outlined
                            :rules="[v => !!v || 'Subject is required for email']"
                            prepend-inner-icon="mdi-format-title"
                            class="mb-2 mb-sm-3"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                          <v-text-field
                            v-model="advertising_message.link"
                            label="CTA Link"
                            dense outlined hide-details
                            prepend-inner-icon="mdi-link"
                            class="mb-2 mb-sm-3"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="advertising_message.get_file"
                            label="Attachment"
                            dense outlined hide-details readonly
                            prepend-inner-icon="mdi-paperclip"
                            class="mb-2 mb-sm-3"
                          >
                            <template v-slot:append>
                              <v-btn icon x-small @click="copyFileUrl" title="Copy URL">
                                <v-icon x-small>mdi-content-copy</v-icon>
                              </v-btn>
                            </template>
                          </v-text-field>
                        </v-col>
                        <v-col cols="12">
                          <v-textarea
                            v-model="advertising_message.content"
                            label="Message body *"
                            dense outlined
                            :rows="$vuetify.breakpoint.xsOnly ? 2 : 3"
                            auto-grow
                            :rules="[v => !!v || 'Message body is required for email']"
                            prepend-inner-icon="mdi-text"
                            class="mb-0"
                          ></v-textarea>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-tab-item>
                </v-tabs-items>

                <!-- Live Preview (always visible when there's content) -->
                <v-expand-transition>
                  <v-card v-if="hasMessage" outlined class="preview-card mt-3 mt-md-4" color="grey lighten-4">
                    <v-card-text class="pa-3 pa-sm-4">
                      <div class="d-flex align-center mb-2 mb-sm-3">
                        <v-icon x-small color="primary" class="mr-2">mdi-eye</v-icon>
                        <span class="text-caption font-weight-bold primary--text text--darken-2">LIVE PREVIEW</span>
                        <v-spacer></v-spacer>
                        <v-chip x-small outlined color="grey">{{ messageCharCount }} chars</v-chip>
                      </div>
                      <v-alert outlined color="primary" class="mb-0 preview-alert" :icon="$vuetify.breakpoint.xsOnly ? false : 'mdi-cellphone-message'">
                        <div class="font-weight-bold text-body-2 text-sm-body-1">{{ advertising_message.title || 'No subject' }}</div>
                        <div class="text-body-2 mt-1 mt-sm-2 preview-content">{{ advertising_message.content || 'No content yet...' }}</div>
                        <div v-if="advertising_message.link" class="mt-2 mt-sm-3">
                          <v-chip x-small color="primary" outlined class="preview-link-chip">
                            <v-icon left x-small>mdi-open-in-new</v-icon>
                            <span class="text-truncate" style="max-width: 160px;">{{ advertising_message.link }}</span>
                          </v-chip>
                        </div>
                        <div v-if="advertising_message.get_file" class="mt-1 mt-sm-2">
                          <v-chip x-small color="orange" outlined>
                            <v-icon left x-small>mdi-paperclip</v-icon>
                            Attachment
                          </v-chip>
                        </div>
                      </v-alert>
                    </v-card-text>
                  </v-card>
                </v-expand-transition>
              </div>

              <v-divider></v-divider>

              <!-- ── Section 3: Prospects Selection ── -->
              <div class="pa-3 pa-sm-4 pa-md-6">
                <div class="d-flex align-center mb-3 mb-md-4 flex-wrap">
                  <v-avatar size="32" :class="$vuetify.breakpoint.smAndUp ? 'mr-3' : 'mr-2'" color="success lighten-5">
                    <v-icon color="success" small>mdi-account-multiple-check</v-icon>
                  </v-avatar>
                  <div class="flex-grow-1" style="min-width: 0;">
                    <div class="text-subtitle-2 text-sm-subtitle-1 font-weight-bold">Select Prospects</div>
                    <div class="text-caption grey--text">
                      {{ userList.length }} total · {{ selectedProspects.length }} selected
                    </div>
                  </div>
                  <v-chip v-if="selectedProspects.length" x-small color="success" dark class="ml-auto">
                    <v-icon left x-small>mdi-check-circle</v-icon>
                    {{ selectedProspects.length }}
                  </v-chip>
                </div>

                <!-- Stats row -->
                <v-row dense class="mb-4">
                  <v-col cols="6" sm="3">
                    <v-card outlined class="stat-card pa-3 text-center">
                      <v-icon color="primary" size="24" class="mb-1">mdi-account-multiple</v-icon>
                      <div class="text-h6 font-weight-bold primary--text">{{ userList.length }}</div>
                      <div class="text-caption grey--text">Total</div>
                    </v-card>
                  </v-col>
                  <v-col cols="6" sm="3">
                    <v-card outlined class="stat-card pa-3 text-center">
                      <v-icon color="success" size="24" class="mb-1">mdi-check-circle</v-icon>
                      <div class="text-h6 font-weight-bold success--text">{{ sentCount }}</div>
                      <div class="text-caption grey--text">Sent</div>
                    </v-card>
                  </v-col>
                  <v-col cols="6" sm="3">
                    <v-card outlined class="stat-card pa-3 text-center">
                      <v-icon color="info" size="24" class="mb-1">mdi-clock-outline</v-icon>
                      <div class="text-h6 font-weight-bold info--text">{{ pendingCount }}</div>
                      <div class="text-caption grey--text">Pending</div>
                    </v-card>
                  </v-col>
                  <v-col cols="6" sm="3">
                    <v-card outlined class="stat-card pa-3 text-center">
                      <v-icon color="warning" size="24" class="mb-1">mdi-percent</v-icon>
                      <div class="text-h6 font-weight-bold warning--text">{{ completionRate }}%</div>
                      <div class="text-caption grey--text">Completion</div>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- Search & Filters -->
                <v-card outlined class="mb-4 rounded-lg" elevation="0">
                  <v-card-text class="pa-3">
                    <v-row align="center" dense>
                      <v-col cols="12" sm="6" md="4">
                        <v-text-field
                          v-model="search"
                          prepend-inner-icon="mdi-magnify"
                          placeholder="Search prospects..."
                          hide-details dense filled rounded
                          class="search-field"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6" md="4">
                        <v-select
                          v-model="statusFilter"
                          :items="statusFilters"
                          label="Filter by Status"
                          dense outlined hide-details
                          prepend-inner-icon="mdi-filter"
                          class="ml-sm-2"
                        ></v-select>
                      </v-col>
                      <v-col cols="12" md="4" class="text-md-right mt-2 mt-md-0">
                        <v-btn text small color="primary" @click="selectAllVisible" class="mr-2">
                          <v-icon left x-small>mdi-select-all</v-icon> Select All
                        </v-btn>
                        <v-btn text small color="grey" @click="selectedProspects = []">
                          <v-icon left x-small>mdi-select-off</v-icon> Clear
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>

                <!-- Ready to Send Banner -->
                <v-expand-transition>
                  <div v-if="selectedProspects.length > 0 && selectedChannels.length > 0" class="mb-4">
                    <v-sheet color="primary lighten-5" class="pa-4 rounded-lg elevation-1" outlined>
                      <v-row align="center" dense>
                        <v-col cols="12" md="7">
                          <div class="d-flex align-center flex-wrap">
                            <v-icon color="primary" class="mr-2">mdi-rocket-launch</v-icon>
                            <span class="font-weight-bold primary--text mr-2">Ready to send!</span>
                            <v-chip x-small color="primary" dark class="mr-2">
                              {{ selectedProspects.length }} prospect{{ selectedProspects.length > 1 ? 's' : '' }}
                            </v-chip>
                            <span class="text-caption grey--text mr-1">via</span>
                            <v-chip
                              v-for="ch in activeChannelsInfo"
                              :key="ch.value"
                              x-small
                              :color="ch.color"
                              dark
                              class="mr-1"
                            >
                              <v-icon left x-small>{{ ch.icon }}</v-icon>
                              {{ ch.label }}
                            </v-chip>
                          </div>
                          <div v-if="!hasMessage" class="text-caption error--text mt-1">
                            <v-icon x-small color="error">mdi-alert</v-icon>
                            Please compose a message before sending.
                          </div>
                        </v-col>
                        <v-col cols="12" md="5" class="text-md-right mt-2 mt-md-0">
                          <v-btn
                            color="primary"
                            large
                            depressed
                            rounded
                            :disabled="!canSend"
                            :loading="sendingBulk"
                            class="px-8 send-btn"
                            @click="handleSendCampaign"
                          >
                            <v-icon left>mdi-send</v-icon>
                            Send Broadcast
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-sheet>
                  </div>
                </v-expand-transition>

                <!-- Prospects Table -->
                <v-card elevation="0" outlined class="rounded-lg">
                  <v-data-table
                    v-model="selectedProspects"
                    :headers="headers"
                    :items="filteredUserList"
                    item-key="id"
                    show-select
                    :items-per-page="10"
                    :search="search"
                    :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100] }"
                    class="elevation-0 prospects-table"
                    :loading="loading"
                    loading-text="Loading prospects..."
                    no-data-text="No prospects found"
                    no-results-text="No matching prospects"
                  >
                    <template #[`item.prospect`]="{ item }">
                      <div class="d-flex align-center py-2">
                        <v-badge :value="getSentCount(item) > 0" :color="getSentCount(item) >= 2 ? 'success' : 'info'" dot overlap offset-x="4" offset-y="4">
                          <v-avatar size="44" :color="item.user_id ? 'primary' : 'grey lighten-2'" class="mr-3">
                            <span class="white--text text-subtitle-2 font-weight-bold">{{ getInitials(item.name) }}</span>
                          </v-avatar>
                        </v-badge>
                        <div class="d-flex flex-column">
                          <span class="font-weight-semibold text-body-2">{{ item.name }}</span>
                          <div class="d-flex align-center flex-wrap">
                            <v-chip x-small :color="item.profession ? 'blue lighten-5' : 'grey lighten-3'" label class="mr-1 mt-1">
                              {{ item.profession || 'Unknown' }}
                            </v-chip>
                            <span class="text-caption grey--text mt-1">{{ item.phone_number }}</span>
                          </div>
                        </div>
                      </div>
                    </template>

                    <template #[`item.channels`]="{ item }">
                      <div class="d-flex align-center justify-center channel-indicators">
                        <template v-for="ch in getProspectChannelIndicators(item)">
                          <v-tooltip bottom :key="ch.key">
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                v-bind="attrs" v-on="on"
                                :color="ch.available ? ch.color : 'grey lighten-2'"
                                size="20"
                                class="mx-1"
                              >{{ ch.icon }}</v-icon>
                            </template>
                            <span>{{ ch.tooltip }}</span>
                          </v-tooltip>
                        </template>
                      </div>
                    </template>

                    <template #[`item.status`]="{ item }">
                      <v-chip small :color="getStatusColor(item)" :text-color="getStatusTextColor(item)" label class="status-chip">
                        <v-icon left x-small>{{ getStatusIcon(item) }}</v-icon>
                        {{ getStatusLabel(item) }}
                      </v-chip>
                    </template>
                  </v-data-table>
                </v-card>
              </div>


            </v-card>

          </v-col>
        </v-row>
      </v-container>

      <!-- Overlay de chargement -->
      <v-overlay :value="loading" opacity="0.8" z-index="999">
        <v-card class="pa-6 text-center" max-width="320" rounded="lg">
          <v-progress-circular indeterminate color="primary" size="56" width="4" class="mb-4" />
          <div class="text-h6 font-weight-medium mb-1">Loading Prospects</div>
          <div class="text-body-2 grey--text">Fetching your campaign data...</div>
        </v-card>
      </v-overlay>

      <!-- Dialogue de progression -->
      <v-dialog v-model="bulkProgressDialog" max-width="400" persistent>
        <v-card>
          <v-card-title class="headline primary white--text">
            <v-icon left dark>mdi-send</v-icon> Sending Messages
          </v-card-title>
          <v-card-text class="pa-6 text-center">
            <v-progress-circular :value="bulkProgress" size="120" width="8" color="primary" class="mb-4">
              <div class="text-h5 font-weight-bold">{{ bulkProgress }}%</div>
            </v-progress-circular>
            <div class="text-h6 font-weight-medium mb-1">{{ bulkProgressLabel }}</div>
            <div class="text-body-2 grey--text">{{ bulkProgressDetail }}</div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Popup WhatsApp (fichiers non supportés) -->
      <v-dialog v-model="whatsappWarningDialog" max-width="440">
        <v-card rounded="lg">
          <v-card-title class="headline orange darken-1 white--text d-flex align-center">
            <v-icon left dark>mdi-whatsapp</v-icon>
            WhatsApp — Attachment Warning
          </v-card-title>
          <v-card-text class="pa-5">
            <v-alert type="warning" outlined dense class="mb-4" icon="mdi-file-alert">
              <strong>Documents and files cannot be sent directly via WhatsApp</strong> without the WhatsApp Business API.
            </v-alert>
            <p class="body-2 mb-3">
              The message will be sent as <strong>text only</strong>. The file download link will be appended to the message so the recipient can access it manually.
            </p>
            <v-card outlined color="grey lighten-4" class="pa-3 rounded-lg">
              <div class="text-caption grey--text mb-1">Attached file:</div>
              <div class="font-weight-medium text-body-2 text-truncate">
                <v-icon x-small class="mr-1">mdi-paperclip</v-icon>
                {{ advertising_message.get_file || 'No attachment' }}
              </div>
            </v-card>
          </v-card-text>
          <v-card-actions class="pa-4 pt-0">
            <v-spacer></v-spacer>
            <v-btn text @click="whatsappWarningDialog = false">Cancel</v-btn>
            <v-btn color="orange darken-1" dark depressed rounded @click="confirmWhatsAppSend">
              <v-icon left small>mdi-send</v-icon>
              Send without file
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar -->
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" top right>
        <div class="d-flex align-center">
          <v-icon left>{{ snackbar.icon }}</v-icon>
          {{ snackbar.message }}
        </div>
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
        </template>
      </v-snackbar>
    </template>
  </v-container>
</template>

<script>
import axios from 'axios'

export default {
  name: 'CampaignImproved',
  data() {
    return {
      view: 'audience',
      selectedAudience: null,
      adGroups: [],
      audienceInfo: null,

      messageMode: 0, // 0 = template, 1 = custom
      selectedMessageTemplate: null,
      selectedChannels: [],
      channels: [
        { value: 'Email', label: 'Email', icon: 'mdi-email', color: 'orange' },
        { value: 'Whatsapp', label: 'WhatsApp', icon: 'mdi-whatsapp', color: 'green' },
        { value: 'SMS', label: 'SMS', icon: 'mdi-message', color: 'blue' },
        { value: 'webpush', label: 'Web Push', icon: 'mdi-web', color: 'teal' },
      ],

      userList: [],
      advertising_message: {
        title: '',
        content: '',
        link: '',
        get_file: '',
      },
      adMessages: [],
      groupName: '',
      loading: false,
      search: '',
      statusFilter: 'all',
      statusFilters: [
        { text: 'All Prospects', value: 'all' },
        { text: 'Pending', value: 'pending' },
        { text: 'Sent', value: 'sent' },
        { text: 'Multi-sent', value: 'multi' },
      ],
      selectedProspects: [],
      sentStatus: {},
      sendingStatus: {},
      sendingBulk: false,
      bulkProgressDialog: false,
      bulkProgress: 0,
      bulkProgressLabel: '',
      bulkProgressDetail: '',
      snackbar: { show: false, message: '', color: 'success', icon: 'mdi-check-circle' },
      whatsappWarningDialog: false,
      pendingWhatsAppAction: null,

      headers: [
        { text: 'Prospect', value: 'prospect', sortable: false, width: '280px' },
        { text: 'Channels', value: 'channels', sortable: false, align: 'center', width: '140px' },
        { text: 'Status', value: 'status', sortable: false, align: 'center', width: '120px' },
      ],
    }
  },
  computed: {
    hasMessage() {
      return !!(this.advertising_message.title || this.advertising_message.content)
    },
    messageCharCount() {
      return (this.advertising_message.content || '').length
    },
    canSend() {
      return this.selectedProspects.length > 0
        && this.selectedChannels.length > 0
        && this.hasMessage
        && !this.sendingBulk
    },
    filteredUserList() {
      let list = this.userList
      if (this.statusFilter === 'pending')
        list = list.filter(item => !this.sentStatus[item.id] || this.sentStatus[item.id].length === 0)
      else if (this.statusFilter === 'sent')
        list = list.filter(item => { const s = this.sentStatus[item.id] || []; return s.length > 0 && s.length < 2 })
      else if (this.statusFilter === 'multi')
        list = list.filter(item => (this.sentStatus[item.id] || []).length >= 2)
      return list
    },
    sentCount() {
      return this.userList.filter(item => (this.sentStatus[item.id] || []).length > 0).length
    },
    pendingCount() {
      return this.userList.filter(item => !this.sentStatus[item.id] || this.sentStatus[item.id].length === 0).length
    },
    completionRate() {
      if (this.userList.length === 0) return 0
      return Math.round((this.sentCount / this.userList.length) * 100)
    },
    activeChannelsInfo() {
      return this.channels.filter(ch => this.selectedChannels.includes(ch.value))
    },
  },
  watch: {
    selectedAudience(val) {
      const group = this.adGroups.find(g => g.id === val)
      this.audienceInfo = group || null
    },
    messageMode(val) {
      if (val === 1) {
        if (!this.advertising_message.link) {
          this.loadDefaultReferralLink()
        }
      }
    },
  },
  mounted() {
    this.fetchAdGroups()
    this.fetchAdMessages()
    document.title = 'TopUp | Broadcast'
  },
  methods: {
    fetchAdGroups() {
      axios.get('/api/v1/topup/get/adgroups/')
        .then(r => { 
          this.adGroups = r.data
          if (this.adGroups && this.adGroups.length > 0 && !this.selectedAudience) {
            const defaultGroup = this.adGroups.find(g => g.name && g.name.trim().toLowerCase() === 'my prospects')
            if (defaultGroup) {
              this.selectedAudience = defaultGroup.id
            } else {
              this.selectedAudience = this.adGroups[0].id
            }
          }
        })
        .catch(e => this.showToast('Failed to load groups', 'error'))
    },
    fetchAdMessages() {
      axios.get('/api/v1/topup/get/messages_list/')
        .then(r => { this.adMessages = r.data })
        .catch(e => this.showToast('Failed to load message templates', 'error'))
    },
    async loadDefaultReferralLink() {
      try {
        const res = await axios.get('/api/v1/topup/my-agent-info/')
        if (res.data && res.data.serial_no) {
          const serial = res.data.serial_no
          const baseUrl = "https://myapp.pettycashglobal.com"
          const payload = `pc_ref:${serial}`
          const encoded = btoa(payload)
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '')
          this.advertising_message.link = `${baseUrl}/create_business_account2?ref=${encoded}`
        }
      } catch (err) {
        console.error('Failed to load agent info for referral link', err)
      }
    },
    startCampaign() {
      if (!this.selectedAudience) return
      this.groupName = this.audienceInfo?.name || ''
      this.view = 'send'
      this.loadProspects()
    },
    async loadProspects() {
      this.loading = true
      try {
        const fd = new FormData()
        fd.append('group_id', this.selectedAudience)
        fd.append('message_id', '0')
        fd.append('message_means', '')
        fd.append('start_date', '')
        fd.append('end_date', '')
        const resp = await axios.post('/api/v1/topup/get/prospects/ad_message/', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        this.userList = (resp.data.prospects || []).map(p => ({
          ...p,
          user_id: p.user_id || null,
          email: p.email || '',
          phone_number: p.phone_number || '',
        }))
        if (resp.data.advertising_message) {
          this.advertising_message = resp.data.advertising_message
        }
      } catch (err) {
        this.showToast('Could not load prospects', 'error')
      } finally {
        this.loading = false
      }
    },
    applyMessageTemplate(templateId) {
      if (!templateId) return
      const tpl = this.adMessages.find(m => m.id === templateId)
      if (tpl) {
        this.advertising_message.title = tpl.title || ''
        this.advertising_message.content = tpl.content || ''
        this.advertising_message.link = tpl.link || ''
        this.advertising_message.get_file = tpl.get_file || ''
      }
    },
    // Returns channel indicators for the prospect row (shows which channels are available for them)
    getProspectChannelIndicators(prospect) {
      const active = this.selectedChannels
      return [
        {
          key: 'whatsapp', icon: 'mdi-whatsapp', color: 'green',
          available: active.includes('Whatsapp'),
          tooltip: active.includes('Whatsapp')
            ? (prospect.phone_number ? 'WhatsApp available' : 'Missing phone number')
            : 'WhatsApp not selected',
        },
        {
          key: 'email', icon: 'mdi-email', color: 'orange',
          available: active.includes('Email'),
          tooltip: active.includes('Email')
            ? (prospect.email ? 'Email available' : 'Missing email address')
            : 'Email not selected',
        },
        {
          key: 'sms', icon: 'mdi-message', color: 'blue',
          available: active.includes('SMS'),
          tooltip: active.includes('SMS')
            ? (prospect.phone_number ? 'SMS available' : 'Missing phone number')
            : 'SMS not selected',
        },
        {
          key: 'webpush', icon: 'mdi-web', color: 'teal',
          available: active.includes('webpush'),
          tooltip: active.includes('webpush') ? 'Web Push available' : 'Web Push not selected',
        },
      ]
    },
    buildMessage() {
      const parts = []
      if (this.advertising_message.title) parts.push(this.advertising_message.title)
      if (this.advertising_message.content) parts.push(this.advertising_message.content)
      if (this.advertising_message.link) parts.push(this.advertising_message.link)
      return parts.join('\n\n')
    },

    // ---- UNIFIED SEND ----
    handleSendCampaign() {
      if (!this.canSend) return

      // Validate subject + body for email channel
      const hasEmail = this.selectedChannels.includes('Email')
      if (hasEmail) {
        if (!this.advertising_message.title || !this.advertising_message.title.trim()) {
          return this.showToast('Subject is required to send emails. Please fill in the Subject field.', 'warning')
        }
        if (!this.advertising_message.content || !this.advertising_message.content.trim()) {
          return this.showToast('Message body is required to send emails. Please fill in the Message body.', 'warning')
        }
      }

      // Check if WhatsApp is selected and there's a file attachment
      const hasWhatsApp = this.selectedChannels.includes('Whatsapp')
      const hasFile = !!this.advertising_message.get_file

      if (hasWhatsApp && hasFile) {
        this.pendingWhatsAppAction = () => this.executeSendCampaign()
        this.whatsappWarningDialog = true
      } else {
        this.executeSendCampaign()
      }
    },

    async executeSendCampaign() {
      const targets = [...this.selectedProspects]
      const activeChannels = [...this.selectedChannels]
      const totalSteps = targets.length * activeChannels.length
      let currentStep = 0

      this.sendingBulk = true
      this.bulkProgressDialog = true
      this.bulkProgressLabel = 'Sending Broadcast...'
      this.bulkProgressDetail = `0 of ${totalSteps} operations`

      for (const prospect of targets) {
        for (const channel of activeChannels) {
          currentStep++
          this.bulkProgress = Math.round((currentStep / totalSteps) * 100)
          this.bulkProgressDetail = `${currentStep} of ${totalSteps}: ${prospect.name} via ${channel}`

          try {
            if (channel === 'Whatsapp' && prospect.phone_number) {
              await this.sendWhatsApp(prospect, !!this.advertising_message.get_file)
            } else if (channel === 'Email' && prospect.email) {
              await this.sendEmail(prospect)
            } else if (channel === 'SMS' && prospect.phone_number) {
              await this.sendSMS(prospect)
            } else if (channel === 'webpush') {
              // Web push logic — placeholder
              await this.delay(200)
              this.markSent(prospect.id, 'webpush')
            }
          } catch (err) {
            console.error(`Failed to send ${channel} to ${prospect.name}`, err)
          }

          await this.delay(200)
        }
      }

      this.bulkProgressLabel = 'Broadcast Sent!'
      this.bulkProgressDetail = `Completed! ${totalSteps} operations processed`
      this.bulkProgress = 100

      setTimeout(() => {
        this.bulkProgressDialog = false
        this.bulkProgress = 0
        this.sendingBulk = false
        this.selectedProspects = []
        this.showToast(`Broadcast sent to ${targets.length} prospects via ${activeChannels.length} channel(s)`, 'success')
      }, 2000)
    },

    // ---- Gestion WhatsApp avec popup ----
    confirmWhatsAppSend() {
      this.whatsappWarningDialog = false
      if (this.pendingWhatsAppAction) {
        this.pendingWhatsAppAction()
        this.pendingWhatsAppAction = null
      }
    },
    async sendWhatsApp(prospect, includeFileLink = false) {
      const phone = this.formatPhone(prospect.phone_number)
      if (!phone) return this.showToast('No valid phone number', 'warning')
      this.setSending(prospect.id, 'whatsapp', true)
      let message = this.buildMessage()
      if (this.advertising_message.get_file && includeFileLink) {
        message += `\n\nDownload: ${this.advertising_message.get_file}`
      }
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank')
      await this.delay(500)
      this.markSent(prospect.id, 'whatsapp')
      this.setSending(prospect.id, 'whatsapp', false)
    },
    // ---- Email ----
    async sendEmail(prospect) {
      if (!prospect.email) return
      // Backend requires both subject and body
      const subject = (this.advertising_message.title || '').trim()
      const body = (this.advertising_message.content || '').trim()
      if (!subject || !body) {
        this.showToast(`Cannot send email to ${prospect.name}: subject and body are required`, 'warning')
        return
      }
      this.setSending(prospect.id, 'email', true)
      try {
        await axios.post('/api/v1/topup/send/bulk-email/', {
          emails: [prospect.email.trim()],
          subject: subject,
          body: body,
          link: (this.advertising_message.link || '').trim(),
          file_url: (this.advertising_message.get_file || '').trim(),
        })
        this.markSent(prospect.id, 'email')
      } catch {
        this.showToast(`Failed to send email to ${prospect.name}`, 'error')
      } finally {
        this.setSending(prospect.id, 'email', false)
      }
    },
    // ---- SMS ----
    async sendSMS(prospect) {
      const phone = this.formatPhone(prospect.phone_number)
      if (!phone) return
      this.setSending(prospect.id, 'sms', true)
      window.open(`sms:${phone}?body=${encodeURIComponent(this.buildMessage())}`, '_blank')
      await this.delay(500)
      this.markSent(prospect.id, 'sms')
      this.setSending(prospect.id, 'sms', false)
    },
    // ---- Utilitaires ----
    formatPhone(raw) {
      if (!raw) return ''
      let d = raw.replace(/\D/g, '')
      if (d.startsWith('0')) d = d.substring(1)
      if (d.length === 9) d = '237' + d
      return d ? `+${d}` : ''
    },
    getInitials(name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    },
    copyFileUrl() {
      navigator.clipboard.writeText(this.advertising_message.get_file || '')
      this.showToast('File URL copied', 'success')
    },
    markSent(id, channel) {
      if (!this.sentStatus[id]) this.$set(this.sentStatus, id, [])
      if (!this.sentStatus[id].includes(channel)) this.sentStatus[id].push(channel)
    },
    setSending(id, channel, status) {
      if (!this.sendingStatus[id]) this.$set(this.sendingStatus, id, {})
      this.$set(this.sendingStatus[id], channel, status)
    },
    isSending(id, channel) {
      return this.sendingStatus[id]?.[channel] || false
    },
    getSentCount(item) {
      return (this.sentStatus[item.id] || []).length
    },
    getStatusColor(item) {
      const c = this.getSentCount(item)
      return c === 0 ? 'grey lighten-4' : c >= 2 ? 'success lighten-4' : 'info lighten-4'
    },
    getStatusTextColor(item) {
      const c = this.getSentCount(item)
      return c === 0 ? 'grey darken-1' : c >= 2 ? 'success darken-2' : 'info darken-2'
    },
    getStatusLabel(item) {
      const c = this.getSentCount(item)
      return c === 0 ? 'Pending' : c >= 2 ? 'Multi-sent' : 'Sent'
    },
    getStatusIcon(item) {
      const c = this.getSentCount(item)
      return c === 0 ? 'mdi-clock-outline' : c >= 2 ? 'mdi-check-all' : 'mdi-check'
    },
    selectAllVisible() {
      this.selectedProspects = [...this.filteredUserList]
    },
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    showToast(msg, type) {
      this.snackbar = {
        show: true,
        message: msg,
        color: type === 'success' ? 'success' : type === 'warning' ? 'warning' : 'error',
        icon: type === 'success' ? 'mdi-check-circle' : type === 'warning' ? 'mdi-alert' : 'mdi-alert-circle',
      }
    },
  },
}
</script>

<style scoped>
.hero-header { border-radius: 0 0 24px 24px; position: relative; overflow: hidden; }
.hero-header::after { content: ''; position: absolute; top: -50%; right: -20%; width: 60%; height: 200%; background: rgba(255,255,255,0.05); border-radius: 50%; }

.unified-campaign-card { border-radius: 20px; overflow: hidden; }

.preview-card { border-radius: 12px; border: 1px dashed rgba(0,0,0,0.15) !important; }
.preview-content { white-space: pre-wrap; word-break: break-word; }
.preview-link-chip { max-width: 100%; }
.preview-alert { overflow: hidden; }

.stat-card { border-radius: 12px; transition: transform 0.2s, box-shadow 0.2s; }
.stat-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important; }

.prospects-table ::v-deep th {
  font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px;
  color: rgba(0,0,0,0.6) !important; font-weight: 600 !important; background: #f8f9fa !important;
}
.prospects-table ::v-deep td { border-bottom: 1px solid rgba(0,0,0,0.04) !important; }

.gap-2 { gap: 8px; }
.channel-indicators { gap: 2px; }
.status-chip { font-weight: 500; }
.truncate { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 200px; }
.search-field { max-width: 100%; }

/* Channel cards */
.channel-card {
  cursor: pointer; transition: all 0.25s; border: 2px solid transparent !important; position: relative;
  border-radius: 12px !important;
}
.channel-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important; }
.channel-card--active { border-color: var(--v-primary-base) !important; box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important; }
.check-icon { position: absolute; top: 4px; right: 4px; }
.channel-label { font-size: 0.75rem; line-height: 1.2; }

/* Message tabs */
.message-tabs { border-radius: 12px; overflow: hidden; }
.message-tabs ::v-deep .v-tab { letter-spacing: 0; padding: 0 12px; }
.message-tabs ::v-deep .v-tab--active { background: rgba(var(--v-primary-base-rgb), 0.08); }
.tab-label { font-size: 0.8125rem; }

/* Send footer */
.send-footer {
  border-radius: 0 0 20px 20px;
}
.send-btn {
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: none;
  font-size: 1rem;
}
.send-btn:not(.v-btn--disabled) {
  box-shadow: 0 4px 16px rgba(var(--v-primary-base-rgb), 0.35);
}

/* ===== MOBILE RESPONSIVE ===== */
@media (max-width: 600px) {
  .hero-header { border-radius: 0 0 16px 16px; padding: 20px 12px !important; }
  .hero-header h1 { font-size: 1.4rem !important; }
  .hero-header p { font-size: 0.8rem !important; }
  .hero-header .v-avatar { width: 48px !important; height: 48px !important; min-width: 48px !important; }
  .hero-header .v-avatar .v-icon { font-size: 26px !important; }

  .unified-campaign-card { border-radius: 14px; }

  .channel-card { border-radius: 8px !important; }
  .channel-card .v-card__text { padding: 6px 4px !important; }
  .channel-label { font-size: 0.625rem !important; }
  .check-icon { top: 2px; right: 2px; }

  .message-tabs ::v-deep .v-tab { padding: 0 8px; min-width: auto; font-size: 0.75rem; }
  .message-tabs ::v-deep .v-tab .v-icon { font-size: 14px !important; margin-right: 4px !important; }
  .tab-label { font-size: 0.72rem !important; }

  .stat-card { border-radius: 8px; }
  .stat-card .text-h6 { font-size: 1rem !important; }
  .stat-card .v-icon { font-size: 20px !important; }

  .send-footer {
    position: sticky;
    bottom: 0;
    z-index: 10;
    border-radius: 0 0 14px 14px;
    padding: 12px !important;
  }
  .send-btn { font-size: 0.875rem; padding: 0 16px !important; }

  .prospects-table ::v-deep .v-data-table__mobile-row { padding: 4px 8px; }
  .preview-card .v-card__text { padding: 10px !important; }
}

@media (max-width: 400px) {
  .channel-label { font-size: 0.5625rem !important; }
  .hero-header h1 { font-size: 1.2rem !important; }
}
</style>